import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaChevronLeft,
  FaChevronRight,
  FaList,
  FaSearch,
  FaTh,
  FaTimes,
} from "react-icons/fa";
import { LuChevronRight, LuFactory, LuFileText, LuGavel, LuUsers } from "react-icons/lu";
import HubHomeFooter from "../hub/HubHomeFooter";
import MarketplaceAdSection from "./MarketplaceAdSection";
import ContractManufacturerGridCard from "./ContractManufacturerGridCard";
import ContractManufacturerListRow from "./ContractManufacturerListRow";
import ContractManufacturersFiltersPanel from "./ContractManufacturersFiltersPanel";
import CommonService from "../../services/common.service";
import { buildMarketplaceModuleTabs } from "../../constants/marketplaceModuleTabs";
import { goToBuyerRfqPost, goToManufacturerRegistration, goToQuickRfqCreation } from "../../utils/buyerRfqRedirect";
import { useShowListAsSupplierButton } from "../../hooks/useShowListAsSupplierButton";

const CM_MANUFACTURER_SORT_OPTIONS = [
  { value: "relevance", label: "Best Match" },
  { value: "name", label: "Name A-Z" },
  { value: "rating", label: "Highest Rated" },
];

function formatStatCount(value) {
  return (Number(value) || 0).toLocaleString();
}

function cmIndustryDescription(name) {
  return `Browse verified contract manufacturers for ${name}. Compare production capacity, response times, certifications, and request quotes from trusted manufacturing partners.`;
}

function CmIndustryHeroStats({ industry }) {
  const manufacturers = industry?.supplier_count ?? industry?.count ?? 0;
  const products = industry?.product_count ?? 0;
  const verified = industry?.verified_count ?? industry?.buyer_request_count ?? 0;
  const rfqs = industry?.rfq_count ?? 0;

  const pills = [
    { value: manufacturers, label: "manufacturers", icon: LuUsers, iconClass: "explore-subcat-products-hero__stat-icon--suppliers" },
    { value: products, label: "products", icon: LuFactory, iconClass: "explore-subcat-products-hero__stat-icon--cm" },
    { value: verified, label: "verified partners", icon: LuFileText, iconClass: "explore-subcat-products-hero__stat-icon--buyer-reqs" },
    { value: rfqs, label: "active RFQs", icon: LuGavel, iconClass: "explore-subcat-products-hero__stat-icon--auctions" },
  ];

  return (
    <div className="explore-subcat-products-hero__stats">
      {pills.map((pill) => {
        const Icon = pill.icon;
        return (
          <div key={pill.label} className="explore-subcat-products-hero__stat-pill">
            <div className={`explore-subcat-products-hero__stat-icon ${pill.iconClass}`}>
              <Icon aria-hidden />
            </div>
            <span className="explore-subcat-products-hero__stat-text">
              <strong>{formatStatCount(pill.value)}</strong> {pill.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function ExploreCmIndustryManufacturersBrowse({
  industry,
  manufacturers = [],
  loading = false,
  applied,
  pagination = {},
  pageSize,
  pageNumbers = [],
  filters,
  onFiltersChange,
  onClearFilters,
  filterOptions = {},
  industryFilterOptions = [],
  rfqSentCompanyIds = new Set(),
  onSearchSubmit,
  onSortChange,
  onGoToPage,
  onChangePageSize,
  onToggleCompare,
  compareIds = [],
  advertisementBanners = [],
  onBreadcrumbNavigate,
}) {
  const navigate = useNavigate();
  const showListAsSupplier = useShowListAsSupplierButton();
  const [localSearch, setLocalSearch] = useState(filters?.search || "");
  const [viewMode, setViewMode] = useState("list");
  const [dimensionUnitOptions, setDimensionUnitOptions] = useState([]);

  useEffect(() => {
    setLocalSearch(filters?.search || "");
  }, [filters?.search]);

  useEffect(() => {
    let cancelled = false;
    CommonService.get("/master/product-catalogue-details")
      .then((res) => {
        if (cancelled) return;
        const rows = res?.data?.master_diamension_unit || [];
        setDimensionUnitOptions(
          rows
            .map((du) => ({
              value: du.dimention_unit_id ?? du.dimension_unit_id ?? du.unit_id ?? du.id ?? du.value,
              label: du.dimention_unit ?? du.dimension_unit ?? du.unit_name ?? du.unit ?? du.label ?? du.name,
            }))
            .filter((opt) => opt.value != null && opt.label != null && String(opt.label).trim() !== ""),
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const industryName = industry?.name || "Contract Manufacturing";
  const showingTotal = pagination.total || manufacturers.length || 0;
  const moduleTabs = useMemo(
    () =>
      buildMarketplaceModuleTabs({
        activeKey: "cm",
        counts: {
          products: industry?.product_count ?? 0,
          buyerReqs: industry?.rfq_count ?? 0,
          auctions: industry?.auction_count ?? 0,
          cm: showingTotal || industry?.count || 0,
        },
      }),
    [industry, showingTotal],
  );

  return (
    <>
      <section className="explore-subcat-products-hero">
        <div className="explore-subcat-products-hero__inner">
          <button
            type="button"
            className="explore-subcat-products-hero__back"
            onClick={() => onBreadcrumbNavigate?.("cm-industries")}
          >
            <FaChevronLeft aria-hidden /> Back
          </button>
          <nav className="explore-subcat-products-hero__breadcrumb" aria-label="Breadcrumb">
            <Link to="/home">Home</Link>
            <LuChevronRight className="explore-subcat-products-hero__breadcrumb-sep" aria-hidden />
            <button
              type="button"
              className="explore-subcat-products-hero__breadcrumb-link"
              onClick={() => onBreadcrumbNavigate?.("categories")}
            >
              Categories
            </button>
            <LuChevronRight className="explore-subcat-products-hero__breadcrumb-sep" aria-hidden />
            <button
              type="button"
              className="explore-subcat-products-hero__breadcrumb-link"
              onClick={() => onBreadcrumbNavigate?.("cm-industries")}
            >
              Contract Manufacturing
            </button>
            <LuChevronRight className="explore-subcat-products-hero__breadcrumb-sep" aria-hidden />
            <span className="is-current">{industryName}</span>
          </nav>

          <div className="explore-subcat-products-hero__main">
            <div className="explore-subcat-products-hero__intro">
              <h1>{industryName}</h1>
              <p>{cmIndustryDescription(industryName)}</p>
            </div>
            <CmIndustryHeroStats industry={industry} />
          </div>
        </div>
      </section>

      <div className="explore-subcat-products-tabs-bar">
        <div className="explore-subcat-products-tabs-bar__inner">
          <nav className="explore-subcat-products-tabs" aria-label="Listing types">
            {moduleTabs.map((tab) =>
              tab.active ? (
                <span key={tab.key} className="explore-subcat-products-tabs__item is-active">
                  {tab.label}
                </span>
              ) : (
                <Link key={tab.key} to={tab.href} className="explore-subcat-products-tabs__item">
                  {tab.label}
                </Link>
              ),
            )}
          </nav>
        </div>
      </div>

      <section className="explore-subcat-products-controls">
        <div className="explore-subcat-products-controls__inner">
          <div className="explore-subcat-products-controls__top-row">
            <form
              className="explore-subcat-products-controls__search"
              onSubmit={(e) => {
                e.preventDefault();
                onSearchSubmit?.(localSearch);
              }}
            >
              <FaSearch aria-hidden />
              <input
                type="search"
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
                placeholder={`Search ${industryName.toLowerCase()} manufacturers, location, capacity…`}
                aria-label={`Search ${industryName} manufacturers`}
              />
              {localSearch ? (
                <button
                  type="button"
                  className="explore-subcat-products-controls__clear"
                  aria-label="Clear search"
                  onClick={() => {
                    setLocalSearch("");
                    onSearchSubmit?.("");
                  }}
                >
                  <FaTimes aria-hidden />
                </button>
              ) : null}
            </form>

            <div className="explore-subcat-products-controls__sort" role="group" aria-label="Sort manufacturers">
              {CM_MANUFACTURER_SORT_OPTIONS.map((opt) => {
                const effectiveSort = applied.sort || "relevance";
                const isActive = effectiveSort === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={`explore-subcat-products-controls__sort-pill${isActive ? " is-active" : ""}`}
                    aria-pressed={isActive}
                    onClick={() => onSortChange?.(opt.value)}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="explore-subcat-products-controls__actions">
            <button
              type="button"
              className="explore-subcat-products-controls__btn explore-subcat-products-controls__btn--orange"
              onClick={() => goToBuyerRfqPost(navigate)}
            >
              Post Buying Requirement
            </button>
            {showListAsSupplier ? (
              <button
                type="button"
                className="explore-subcat-products-controls__btn explore-subcat-products-controls__btn--navy"
                onClick={() => goToManufacturerRegistration(navigate)}
              >
                List as Manufacturer
              </button>
            ) : null}
            <button
              type="button"
              className="explore-subcat-products-controls__btn explore-subcat-products-controls__btn--blue"
              onClick={() => goToQuickRfqCreation(navigate)}
            >
              Start RFQ
            </button>
          </div>
        </div>
      </section>

      <div className="explore-subcat-products-main">
        <ContractManufacturersFiltersPanel
          filters={filters}
          onChange={onFiltersChange}
          onClear={onClearFilters}
          stateFilterOptions={filterOptions.stateFilterOptions || []}
          cityFilterOptions={filterOptions.cityFilterOptions || []}
          cmTypeFilterOptions={filterOptions.cmTypeFilterOptions || []}
          facilityTypeFilterOptions={filterOptions.facilityTypeFilterOptions || []}
          sidebarIndustryOptions={filterOptions.sidebarIndustryOptions || []}
          sidebarCertFilterOptions={filterOptions.sidebarCertFilterOptions || []}
        />

        <section className="explore-subcat-products-content">
          <div className="explore-subcat-products-content__head">
            <p className="explore-subcat-products-content__count">
              {showingTotal > 0
                ? `${showingTotal.toLocaleString()} result${showingTotal === 1 ? "" : "s"} in ${industryName} — Manufacturers`
                : `0 results in ${industryName} — Manufacturers`}
            </p>
            <div className="explore-subcat-products-view-toggle" aria-label="View mode">
              <button
                type="button"
                className={`explore-subcat-products-view-toggle__btn${viewMode === "list" ? " is-active" : ""}`}
                onClick={() => setViewMode("list")}
                aria-pressed={viewMode === "list"}
                aria-label="Horizontal list view"
                title="Horizontal list view"
              >
                <FaList aria-hidden />
              </button>
              <button
                type="button"
                className={`explore-subcat-products-view-toggle__btn${viewMode === "grid" ? " is-active" : ""}`}
                onClick={() => setViewMode("grid")}
                aria-pressed={viewMode === "grid"}
                aria-label="Vertical grid view"
                title="Vertical grid view"
              >
                <FaTh aria-hidden />
              </button>
            </div>
          </div>

          <div className={`explore-subcat-products-list explore-subcat-products-list--${viewMode}`}>
            {loading && manufacturers.length === 0
              ? Array.from({ length: Math.min(pageSize, viewMode === "grid" ? 8 : 6) }).map((_, idx) => (
                  <div
                    key={idx}
                    className={
                      viewMode === "grid"
                        ? "marketplace-product-grid-card marketplace-product-grid-card--skeleton"
                        : "marketplace-product-row marketplace-product-row--skeleton"
                    }
                    aria-hidden
                  />
                ))
              : null}
            {!loading || manufacturers.length
              ? manufacturers.map((item) =>
                  viewMode === "grid" ? (
                    <ContractManufacturerGridCard
                      key={item.company_id}
                      item={item}
                      dimensionUnitOptions={dimensionUnitOptions}
                      rfqSent={rfqSentCompanyIds.has(Number(item.company_id))}
                      onToggleCompare={onToggleCompare}
                      compared={compareIds.includes(Number(item.company_id))}
                    />
                  ) : (
                    <ContractManufacturerListRow
                      key={item.company_id}
                      item={item}
                      industryFilterOptions={industryFilterOptions}
                      dimensionUnitOptions={dimensionUnitOptions}
                      rfqSent={rfqSentCompanyIds.has(Number(item.company_id))}
                      onToggleCompare={onToggleCompare}
                      compared={compareIds.includes(Number(item.company_id))}
                    />
                  ),
                )
              : null}
            {!loading && manufacturers.length === 0 ? (
              <p className="explore-categories-empty">No contract manufacturers match your filters yet.</p>
            ) : null}
          </div>

          {pagination.total_pages > 0 ? (
            <div className="product-marketplace-pagination-wrap">
              <nav className="product-marketplace-pagination" aria-label="Pagination">
                <button
                  type="button"
                  disabled={Number(applied.page) <= 1}
                  onClick={() => onGoToPage?.(Number(applied.page) - 1)}
                >
                  <FaChevronLeft aria-hidden /> Previous
                </button>
                {pageNumbers.map((page, idx) =>
                  page === "…" ? (
                    <span key={`ellipsis-${idx}`} className="product-marketplace-pagination__ellipsis">
                      …
                    </span>
                  ) : (
                    <button
                      key={page}
                      type="button"
                      className={Number(applied.page) === page ? "is-active" : ""}
                      onClick={() => onGoToPage?.(page)}
                    >
                      {page}
                    </button>
                  ),
                )}
                <button
                  type="button"
                  disabled={Number(applied.page) >= pagination.total_pages}
                  onClick={() => onGoToPage?.(Number(applied.page) + 1)}
                >
                  Next <FaChevronRight aria-hidden />
                </button>
              </nav>
              <label className="product-marketplace-page-size">
                Show
                <select
                  value={pageSize}
                  onChange={(e) => onChangePageSize?.(Number(e.target.value))}
                  aria-label="Manufacturers per page"
                >
                  {[12, 24, 36, 48].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                per page
              </label>
            </div>
          ) : null}
        </section>
      </div>

      <MarketplaceAdSection
        banners={advertisementBanners}
        ariaLabel="Marketplace contract manufacturing promotional banners"
        hideFallback
      />

      <HubHomeFooter />
    </>
  );
}
